import {createSelector} from 'reselect';
import {pathOr, compose, isEmpty, not} from 'ramda';

import {ApplicationStoreType} from 'reducers';
import {QuoteItemType} from 'constants/flow/flowTypesCart';
import {PRODUCT_TYPES} from 'constants/product';
import {getActiveCartItemsCount, getQuoteItems} from 'selectors/cart/cart.js';

/**
 * Takes quote items count
 * @type {SelectorType<number>}
 */
export const getQuoteItemsCount: (store: ApplicationStoreType) => number = pathOr(0, [
    'quoteItems',
    'count',
]);

/**
 * Takes applied coupons list
 * @type {SelectorType<Array<string>>}
 */
export const getAppliedCoupons: (store: ApplicationStoreType) => Array<string> = pathOr(
    [],
    ['quoteItems', 'totals', 'appliedCoupons'],
);

/**
 * Get quote items
 * @param {ApplicationStoreType} store - Current store
 * @returns {string} - Special offer
 */
export const getQuoteItemsSpecialOffer = (store: ApplicationStoreType): string =>
    store.quoteItems?.specialOffers?.[0]?.content_type === 'html'
        ? store.quoteItems.specialOffer
        : '';

/**
 * Determines are there coupons applied
 * @type {SelectorType<boolean>}
 */
export const hasAppliedCoupons: (store: ApplicationStoreType) => boolean = createSelector(
    getAppliedCoupons,
    compose(not, isEmpty),
);

/**
 * Cart consists completely of premium products
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} cart contain a premium condition
 */
export const isCartFullOfPremiumProducts = createSelector(
    getQuoteItems,
    (quoteItems: QuoteItemType[]): boolean =>
        quoteItems.every(quoteItem => {
            if (
                quoteItem.type === PRODUCT_TYPES.ACCESSORY ||
                quoteItem.type === PRODUCT_TYPES.CONTACT_LENSES ||
                quoteItem.type === PRODUCT_TYPES.GIFT
            )
                return false;
            return quoteItem.isPremium;
        }),
);

/**
 * Cart consists completely of lenses products
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} cart contains contact lenses
 */
export const isCartFullOfContactLensesProducts = createSelector(
    getQuoteItems,
    (quoteItems: QuoteItemType[]): boolean =>
        quoteItems.every(({type}) => type === PRODUCT_TYPES.CONTACT_LENSES),
);

/**
 * Returns count of inactive items in quote(includes quantity of each item)
 * @param {ApplicationStoreType} store - Application store
 * @return {Number} inactive items count
 */
export const getInactiveQuoteItemsCount = createSelector(
    getQuoteItemsCount,
    getActiveCartItemsCount,
    (quoteItemsCount, activeQuoteItemsCount): number => quoteItemsCount - activeQuoteItemsCount,
);
